<template>
    <modal-wrapper modalClass="modal--form" :modalName="name" @before-open="beforeOpen">
        <div class="modal__header">
            <h1>{{$t('modals.complete_trajectory.title')}}</h1>
        </div>

        <div class="modal__content">
            <div v-if="errors.length" class="alert--error" >
                <span v-for="err in errors" :key="err"> {{ err }} </span>
            </div>
            <div v-else-if="loading" class="spinner" ></div>

            <form v-else class="form">
                <div class="row">
                    <div class="col-xs-12 col-md-12">
                        <div class="form__item">
                            <i class="form__icon icons icon-hourglass"></i>
                            <vue-select  class="form__select" v-model="trajectory.trajectory_state" :options="finishedStates" label="name" :class="{ 'has-value': trajectory.trajectory_state }"></vue-select>
                            <label class="form__label">{{$t('forms.trajectory.trajectory_state')}}</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-xs-12 col-md-12">
                        <div class="form__item">
                            <i class="form__icon icons icon-info"></i>
                            <input v-model="trajectory.completed_reason" class="form__input--text" type="text" :class="{ 'has-value': trajectory.completed_reason }"/>
                            <label class="form__label">{{$t('forms.trajectory.completed_reason')}}</label>
                        </div>
                    </div>
                </div>

                <h2>{{$t('forms.trajectory.surveys')}}</h2>

                <div class="row">
                    <div class="col-xs-12 col-md-12">
                        <div class="form__item">
                            <div class="checkbox">
                                <input type="checkbox" class="checkbox__cb" id="trajectory_form__send_client_survey" v-model="sendClientSurvey" />
                                <label for="trajectory_form__send_client_survey" class="checkbox__label"> {{$t('forms.trajectory.send_client_survey')}}</label>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row" v-if="sendClientSurvey">
                    <div class="col-xs-12 col-md-12">
                        <div class="form__item">
                            <i class="form__icon icons icon-magnifier"></i>
                            <vue-select label="title" :options="clientSurveyTemplates" v-model="clientSurveyTemplate" class="form__select" :class="{ 'has-value': clientSurveyTemplate }" >
                                <template slot="no-options">{{$t('forms.common.no_results')}}</template>
                            </vue-select>
                            <label class="form__label">{{$t('forms.survey.template')}}</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-xs-12 col-md-12">
                        <div class="form__item">
                            <div class="checkbox">
                                <input type="checkbox" class="checkbox__cb" id="trajectory_form__send_employer_survey" v-model="sendEmployerSurvey" />
                                <label for="trajectory_form__send_employer_survey" class="checkbox__label"> {{$t('forms.trajectory.send_employer_survey')}}</label>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row" v-if="sendEmployerSurvey">
                    <div class="col-xs-12 col-md-12">
                        <div class="form__item">
                            <i class="form__icon icons icon-magnifier"></i>
                            <vue-select label="title" :options="employerSurveyTemplates" v-model="employerSurveyTemplate" class="form__select" :class="{ 'has-value': employerSurveyTemplate }" >
                                <template slot="no-options">{{$t('forms.common.no_results')}}</template>
                            </vue-select>
                            <label class="form__label">{{$t('forms.survey.template')}}</label>
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <div class="modal__footer">
            <div class="actions">
                <button class="actions__action--save pull-right" @click.prevent="completeTrajectory" :class="{disabled: isSaveDisabled}" :disabled="isSaveDisabled">{{$t('modals.complete_trajectory.complete')}}</button>
                <button class="actions__action--cancel" type="button" @click="$modal.hide(name)">{{$t('modals.common.cancel')}}</button>
            </div>
        </div>
    </modal-wrapper>
</template>
<script>
    import Vue from 'vue';
    import { Component } from 'vue-property-decorator';
    import ModalWrapper from '@/components/general/ModalWrapper';

    import TrajectoryForm from '@/components/forms/dashboard/TrajectoryForm';

    import { Trajectory, Survey } from '@/models';

    import { GET_TRAJECTORY } from '@/store/modules/trajectory';
    import { LOAD_TRAJECTORY_RESOURCES, FINISHED_STATES_CODES } from '@/store/modules/trajectory-resources';
    import { GET_SURVEY_TEMPLATES } from '@/store/modules/survey-templates';

    @Component({
        components: {
            TrajectoryForm
        }
    })
    export default class TrajectoryCompleteModal extends Vue {
        name = 'trajectory-complete-modal';
        trajectory = new Trajectory();
        errors = [];
        sendClientSurvey = false;
        clientSurveyTemplate = null;
        sendEmployerSurvey = false;
        employerSurveyTemplate = null;

        beforeOpen(event) {
            this.errors = [];
            this.$store.dispatch(LOAD_TRAJECTORY_RESOURCES);
            this.$store.dispatch(GET_SURVEY_TEMPLATES, {});

            // Deep copy the trajectory to be able to cancel the changes.
            this.trajectory.fill(this.$store.state.trajectory.trajectory);
        }

        get loading () {
            return this.$store.state.trajectory_resources.loading || this.saveLoading;
        }

        get finishedStates() {
            return this.$store.state.trajectory_resources.trajectory_states.filter(state => FINISHED_STATES_CODES.includes(state.code));
        }

        get clientSurveyTemplates() {
            return this.$store.state.survey_templates.templates.filter(template => template.type === 'client');
        }

        get employerSurveyTemplates() {
            return this.$store.state.survey_templates.templates.filter(template => template.type === 'employer');
        }

        get isSaveDisabled() {
            let disabled = !this.trajectory.trajectory_state || !FINISHED_STATES_CODES.includes(this.trajectory.trajectory_state.code);
            disabled = disabled || (this.sendClientSurvey && !this.clientSurveyTemplate);
            disabled = disabled || (this.sendEmployerSurvey && !this.employerSurveyTemplate);
            return disabled;
        }

        completeTrajectory() {
            this.errors = [];
            this.saveLoading = true;
            this.trajectory.save()
                .then(trajectory => {
                    const surveyPromises = [];
                    if (this.sendClientSurvey && this.clientSurveyTemplate) surveyPromises.push(this.addAndStartSurvey(this.clientSurveyTemplate.id));
                    if (this.sendEmployerSurvey && this.employerSurveyTemplate) surveyPromises.push(this.addAndStartSurvey(this.employerSurveyTemplate.id));

                    Promise.all(surveyPromises)
                        .then(res => {
                            this.$notify.success(this.$t('notify.complete_trajectory.completed'));
                            this.$store.dispatch(GET_TRAJECTORY, {});
                            this.$modal.hide(this.name);
                        });
                })
                .catch(err => {
                    if (err.status === 422) {
                        this.errors = err.errors;
                    } else {
                        this.errors = [this.$t('common.unknown_error')];
                    }
                })
                .then(() => this.saveLoading = false);


        }

        trajectoryId() {
            return this.$store.state.trajectory.trajectory.id;
        }

        addAndStartSurvey(templateId) {
            const url = `/trajectories/${this.trajectoryId()}/surveys`;
            const survey = new (Survey.makeModel(url));
            survey.survey_template_id = templateId;
            return survey.save().then(survey => this.startSurvey(survey));
        }

        startSurvey(survey) {
            return this.axios.post(`trajectories/${this.trajectoryId()}/surveys/${survey.id}/start`, {})
                .then(res => this.$notify.success(this.$t('notify.survey.start_success')))
                .catch(err => {
                    this.$notify.error(this.$t('notify.survey.start_error'));
                    // Delete the survey if it fails to be started
                    this.axios.delete(`trajectories/${this.trajectoryId()}/surveys/${survey.id}`).then(this.refetchAndClose);
                });
        }

    }
</script>



// WEBPACK FOOTER //
// src/components/modals/dashboard/TrajectoryCompleteModal.vue